import React, { useState, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { Grid, Box } from "@mui/material";
import { useParams } from "react-router-dom";
import { useEditJobCardMutation, useGetJobCardByReferenceQuery } from "../api/apiSlice";
import CircularProgress from "@mui/material/CircularProgress";
import { getCurrentStep } from "./Stepper/stepperSlice";
import { useSelector } from "react-redux";
import NavbarStepper from "./Stepper/NavBarStepper";
import { useConnectionMonitor } from "./connectionsTest";
import ConnectionAlert from "./ConnectionAlert";
import { useStepperStateRestoration } from "./useStepperStateRestoration";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAlt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import AbortJobButton from "./AbortJobButton";
import { showError } from "../../Global/notifications/notificationsSlice";
import stepSaveMessage from "./stepSaveMessage";
import { useDispatch } from "react-redux";
import useRedoPhotos from "./useRedoPhotos";

const AMBER = "#FDB940";

// Photos for NEW installs (includes pre-install clamp meter reading)
const PHOTO_DEFS_NEW = [
  { name: "clamp_meter_before_photo", label: "Clamp\nMeter" },
  { name: "install_photo_before",     label: "Work Area\nBefore Install" },
  { name: "before_photo_on",          label: "DB Board\nCover ON" },
  { name: "before_photo_off",         label: "DB Board\nCover OFF" },
];

// Photos for Uninstall / Replacement workflows (no clamp meter needed before)
const PHOTO_DEFS_UN_RE = [
  { name: "install_photo_before", label: "Work Area\nBefore Uninstall" },
  { name: "before_photo_on",      label: "DB Board\nCover ON" },
  { name: "before_photo_off",     label: "DB Board\nCover OFF" },
];

function WorkAreaBefore() {
  useStepperStateRestoration();

  const {
    showAlert,
    showWeakWarning,
    handleRetry,
    handleDismiss,
    handleWeakWarningDismiss,
    responseTime,
    avgResponseTime,
    connectionDetails,
    isConnected,
  } = useConnectionMonitor();

  const { job_card_reference_id, serial_number, imsi_number } = useParams();
  const { data: job_card_data, isSuccess: isGetJobCardStateSuccess } = useGetJobCardByReferenceQuery(job_card_reference_id);

  if (!isGetJobCardStateSuccess || !job_card_data) return null;

  const isUninstallType = job_card_data.install_state !== "NEW";
  const pageTitle = isUninstallType
    ? "Before Uninstall Photos"
    : "Before Install Photos";

  const successNavLink = isUninstallType
    ? `/installer/${job_card_reference_id}/${serial_number}/${imsi_number}/${job_card_data.install_state}/`
    : `/installer/${job_card_reference_id}/${serial_number}/${imsi_number}/connection_test_interlude/`;

  const submitState = isUninstallType ? "UNI" : "AMP";
  const photoDefs = isUninstallType ? PHOTO_DEFS_UN_RE : PHOTO_DEFS_NEW;

  return (
    <>
      {showAlert && (
        <ConnectionAlert
          onRetry={handleRetry}
          onClose={handleDismiss}
          connectionDetails={connectionDetails}
          position="top"
          alertType="error"
        />
      )}
      {showWeakWarning && (
        <ConnectionAlert
          onRetry={handleRetry}
          onClose={handleWeakWarningDismiss}
          connectionDetails={connectionDetails}
          position="top"
          alertType="warning"
          responseTime={responseTime || avgResponseTime}
        />
      )}

      <GroupedPhotoCapture
        pageTitle={pageTitle}
        photoDefs={photoDefs}
        successNavLink={successNavLink}
        submitState={submitState}
        isConnected={isConnected}
      />
    </>
  );
}

function GroupedPhotoCapture({ pageTitle, photoDefs, successNavLink, submitState, isConnected }) {
  const step = useSelector(getCurrentStep);
  const { job_card_reference_id } = useParams();
  const navigate = useNavigate();

  const [editJobCard, { isSuccess, isLoading }] = useEditJobCardMutation();
  const dispatch = useDispatch();

  // photos[i] = { file: File, previewUrl: string } | null
  const [photos, setPhotos] = useState(() => photoDefs.map(() => null));
  const { slotNote } = useRedoPhotos(job_card_reference_id, photoDefs, setPhotos);
  const [activeRetakeIndex, setActiveRetakeIndex] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef(null);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Restore photos from localStorage on mount
  useEffect(() => {
    photoDefs.forEach(({ name }, i) => {
      const key = `photo_${job_card_reference_id}_${name}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        try {
          const { dataUrl, fileName } = JSON.parse(saved);
          fetch(dataUrl)
            .then((r) => r.blob())
            .then((blob) => {
              const file = new File([blob], fileName, { type: blob.type });
              const previewUrl = URL.createObjectURL(blob);
              setPhotos((prev) => {
                const next = [...prev];
                next[i] = { file, previewUrl };
                return next;
              });
            })
            .catch(() => localStorage.removeItem(key));
        } catch {
          localStorage.removeItem(key);
        }
      }
    });
  }, [job_card_reference_id]);

  // Save photo to localStorage whenever it changes
  const persistPhoto = (index, file) => {
    const key = `photo_${job_card_reference_id}_${photoDefs[index].name}`;
    const reader = new FileReader();
    reader.onload = (e) => {
      localStorage.setItem(key, JSON.stringify({ dataUrl: e.target.result, fileName: file.name }));
    };
    reader.readAsDataURL(file);
  };

  // Clear localStorage keys on success and navigate
  useEffect(() => {
    if (isSuccess) {
      photoDefs.forEach(({ name }) => {
        localStorage.removeItem(`photo_${job_card_reference_id}_${name}`);
      });
      navigate(successNavLink);
    }
  }, [isSuccess]);

  const capturedCount = photos.filter(Boolean).length;
  const allCaptured = capturedCount === photoDefs.length;

  // Open camera for a specific photo slot
  const openCamera = (index) => {
    setActiveRetakeIndex(index);
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || activeRetakeIndex === null) return;
    const previewUrl = URL.createObjectURL(file);
    setPhotos((prev) => {
      const next = [...prev];
      // Revoke previous preview URL to avoid memory leak
      if (next[activeRetakeIndex]?.previewUrl) {
        URL.revokeObjectURL(next[activeRetakeIndex].previewUrl);
      }
      next[activeRetakeIndex] = { file, previewUrl };
      return next;
    });
    persistPhoto(activeRetakeIndex, file);
    setActiveRetakeIndex(null);
  };

  const handleSubmit = () => {
    if (!allCaptured) return;
    setIsUploading(true);
    const body = new FormData();
    body.append("reference_id", job_card_reference_id);
    body.append("state", submitState);
    // A photo kept from before a redo has no file: the job already has it.
    photoDefs.forEach(({ name }, i) => {
      if (photos[i].file) body.append(name, photos[i].file);
    });
    editJobCard({ body }).unwrap().catch((error) => {
      setIsUploading(false);
      dispatch(showError(stepSaveMessage(error)));
    });
  };

  const openMenu = (e, index) => {
    setMenuAnchor(e.currentTarget);
    setMenuIndex(index);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuIndex(null);
  };

  if (!isMobile) {
    return <p>Sorry, this feature is only available on mobile devices.</p>;
  }

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <NavbarStepper step={step} />

      <Box sx={{ px: 2, pb: "100px" }}>
        {/* Heading row */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1, mb: 2 }}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff" }}>
            {pageTitle}
          </Typography>
          <Box sx={{
            px: 1.2, py: 0.4,
            borderRadius: "20px",
            backgroundColor: allCaptured ? "rgba(34,197,94,0.15)" : "rgba(253,185,64,0.12)",
            border: `1px solid ${allCaptured ? "rgba(34,197,94,0.4)" : "rgba(253,185,64,0.3)"}`,
          }}>
            <Typography sx={{
              fontSize: "0.72rem", fontWeight: 700,
              color: allCaptured ? "#22C55E" : AMBER,
              letterSpacing: "0.05em",
            }}>
              {capturedCount} / {photoDefs.length} CAPTURED
            </Typography>
          </Box>
        </Box>

        {/* Photo tiles row */}
        <Grid container spacing={1.5}>
          {photoDefs.map(({ label }, i) => {
            const photo = photos[i];
            return (
              <Grid item xs={4} key={i}>
                <Box sx={{ position: "relative" }}>
                  {/* Thumbnail / placeholder */}
                  <Box
                    onClick={() => !photo && openCamera(i)}
                    sx={{
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: photo
                        ? "1.5px solid rgba(34,197,94,0.4)"
                        : `1.5px dashed rgba(253,185,64,0.35)`,
                      backgroundColor: "#0d1117",
                      aspectRatio: "1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: photo ? "default" : "pointer",
                    }}
                  >
                    {photo ? (
                      <img
                        src={photo.previewUrl}
                        alt={label}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    ) : (
                      <CameraAltOutlinedIcon sx={{ color: AMBER, opacity: 0.45, fontSize: "1.8rem" }} />
                    )}
                  </Box>

                  {/* "..." retake menu — only shown when photo exists */}
                  {photo && (
                    <IconButton
                      size="small"
                      onClick={(e) => openMenu(e, i)}
                      sx={{
                        position: "absolute", top: 2, right: 2,
                        backgroundColor: "rgba(0,0,0,0.55)",
                        color: "#fff",
                        width: 22, height: 22,
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                  )}
                </Box>

                <Typography sx={{
                  fontSize: "0.62rem", color: "rgba(255,255,255,0.55)",
                  textAlign: "center", mt: "5px", lineHeight: 1.3,
                  whiteSpace: "pre-line",
                }}>
                  {label}
                </Typography>
                {slotNote(i, photos[i])}
              </Grid>
            );
          })}
        </Grid>

        {/* Instruction when photos are missing */}
        {!allCaptured && (
          <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", textAlign: "center", mt: 3 }}>
            Tap a tile to capture that photo
          </Typography>
        )}
      </Box>

      {/* Retake menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{
          sx: { backgroundColor: "#1A1C24", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" },
        }}
      >
        <MenuItem
          onClick={() => { openCamera(menuIndex); closeMenu(); }}
          sx={{ fontSize: "0.875rem" }}
        >
          Retake photo
        </MenuItem>
      </Menu>

      {/* Fixed bottom bar */}
      <Box sx={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        display: "flex", gap: "12px",
        px: "16px", py: "14px",
        background: "rgba(13,14,20,0.95)",
        backdropFilter: "blur(12px)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        <AbortJobButton />
        <Button
          variant="contained"
          disabled={!allCaptured || !isConnected || isLoading || isUploading}
          onClick={handleSubmit}
          disableElevation
          sx={{
            flex: 1,
            backgroundColor: AMBER,
            color: "#000",
            fontWeight: 700,
            borderRadius: "10px",
            py: "13px",
            fontSize: "1rem",
            textTransform: "none",
            border: "none",
            opacity: (!allCaptured || !isConnected || isLoading || isUploading) ? 0.55 : 1,
            "&:hover": { backgroundColor: "#e6a832" },
            "&.Mui-disabled": { opacity: 0.55 },
          }}
          startIcon={(isLoading || isUploading) ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {(isLoading || isUploading) ? "Uploading..." : "Next →"}
        </Button>
      </Box>
    </>
  );
}

export const MemoizedWorkAreaBefore = React.memo(WorkAreaBefore);
